import React, { useEffect, useRef } from 'react'
import { t } from '@lingui/macro'
import { useHistory } from 'react-router-dom'
import {
  NATIVE_PUBLIC_ROUTES,
  nativePublicPathGenerator
} from 'routes/native/PublicRoutes'
import { getChannelSearch } from 'api/search/channelSearch/searchChannels'
import {
  Header,
  SectionHeader,
  Skeleton
} from 'pages/native/onBoarding/components'
import { useInfiniteQueryDataFetcher } from 'hooks/useInfiniteQueryDataFetcher/useInfiniteQueryDataFetcher'
import { TeamList } from './components/TeamList/TeamList'
import { useAppSelector } from 'redux-store/hooks'
import { channelSelector } from 'redux-store/channel/channel.selectors'
import { ChannelTeamItem } from 'pages/native/onBoarding/types/types'
import { NextButton } from './components/NextButton/NextButton'
import { useCompanyName } from 'pages/native/onBoarding/context/OnBoardingContext'
import { SearchInput } from './components/SearchInput/SearchInput'
import useInfiniteScroll from 'hooks/useInfiniteScroll/useInfiniteScroll'
import { useAnalytics } from 'utils/analytics/useAnalytics'

import styles from './FollowPage.module.scss'


const DEFAULT_SEARCH_TERM = '*'
const PAGE_SIZE = 5
const CHANNEL_TYPE = 'team'

export const FollowingPage = () => {
  const history = useHistory()
  const { value: company } = useCompanyName()
  const { trackClarity, trackGtm } = useAnalytics()
  const channel = useAppSelector(channelSelector)

  const isChildChannel = channel?.data?.isChildOfRootChannel || undefined
  const rootChannelSubdomain = channel?.data?.subdomain || undefined
  const isRootPage = channel?.data?.isRootChannel || undefined
  const parentChannelSubdomain =
    channel?.data?.parentChannelSubdomain || undefined
  const rootChannelSlug = isChildChannel
    ? parentChannelSubdomain
    : rootChannelSubdomain
  const searchSubdomain = channel?.data?.subdomain || undefined

  const [fetchedTeams, setFetchedTeams] = React.useState<ChannelTeamItem[]>([])
  const [inputValue, setInputValue] = React.useState<string>('')
  const [searchTerm, setSearchTerm] =
    React.useState<string>(DEFAULT_SEARCH_TERM)

  const isValidToSearch = !!searchTerm

  const params = {
    searchTerm: searchTerm,
    pageNumber: 1,
    channelSlug: searchSubdomain,
    rootChannelSlug: rootChannelSlug,
    pageSize: PAGE_SIZE,
    isRootChannel: isRootPage,
    type: CHANNEL_TYPE
  }

  const containerRef = useRef<any>()

  const {
    data,
    isLoading,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQueryDataFetcher<typeof params, ChannelTeamItem>({
    queryKey: 'onboarding-following-teams-with-search',
    queryFn: getChannelSearch,
    params,
    perPage: PAGE_SIZE,
    enabled: isValidToSearch
  })

  const loadMoreRef = useInfiniteScroll({
    container: containerRef.current,
    fetchNextPage,
    hasNextPage,
    isLoading: isFetchingNextPage
  })

  useEffect(() => {
    if (data) {
      setFetchedTeams(data)
    }
  }, [data])

  const inputOnChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)

    if (newValue === '') {
      setSearchTerm(DEFAULT_SEARCH_TERM)
    }
    if (newValue.length > 2) {
      trackGtm('team_search_performed_app', { query: newValue })
      trackClarity('team_search_query_app', newValue)

      const timeoutId = setTimeout(() => {
        setSearchTerm(newValue)
      }, 250)

      return () => clearTimeout(timeoutId)
    }
  }

  const resetSearchHandler = () => {
    setInputValue('')
    setSearchTerm(DEFAULT_SEARCH_TERM)
  }

  return (
    <div className={styles.PageContainer}>
      <Header
        back={() => history.goBack()}
        skip={() => {
          trackGtm('skip_follow_team_app')
          trackClarity('skip_follow_team_app')
          history.push(
            nativePublicPathGenerator(
              NATIVE_PUBLIC_ROUTES.AUTH.SIGN_UP,
              company
            )
          )
        }}
        steps={4}
        currentStep={2}
      />

      <div className={styles.ContentContainer}>
        <SectionHeader
          title="Follow your favourite teams."
          body={t`Pick a few teams you love to get a personalized feed. (Don’t worry you
        can change this later).`}
        />
        <SearchInput value={inputValue} onChange={inputOnChangeHandler} />

        <div className={styles.TeamList} ref={containerRef}>
          <TeamList
            teams={fetchedTeams}
            isLoading={isLoading || isFetching}
            isFetchingNextPage={isFetchingNextPage}
            isError={isError}
            onResetSearch={resetSearchHandler}
          />
          {hasNextPage && (
            <div ref={loadMoreRef}>
              <Skeleton type="table" rows={2} />
            </div>
          )}
        </div>
      </div>
      {fetchedTeams.length !== 0 && <NextButton />}
    </div>
  )
}
